// ============================================================
// Page Mon Abonnement — Lot client (frontend)
// ============================================================
// Vue du propriétaire sur son abonnement SaaS :
//   - Carte du plan en cours (nom, prix, capacités)
//   - Compte à rebours jours restants + couleur
//   - Historique des paiements
//   - Bouton "Demander un renouvellement" (créé un événement audit)
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  CreditCard, AlertCircle, CheckCircle2, Send, Loader, Calendar,
  Building2, Users, Sparkles, History,
} from 'lucide-react';
import toast from 'react-hot-toast';

function formatMontant(n, devise = 'GNF') {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('fr-FR') + ' ' + devise;
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

function CouleurStatut({ statut, jours }) {
  let bg = 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300';
  let label = 'Actif';
  if (statut === 'lecture_seule') {
    bg = 'bg-red-500/15 border-red-500/30 text-red-300';
    label = 'Lecture seule (expiré)';
  } else if (statut === 'grace') {
    bg = 'bg-orange-500/15 border-orange-500/30 text-orange-300';
    label = `Période de grâce (${jours}j restants)`;
  } else if (jours !== null && jours !== undefined && jours <= 15) {
    bg = 'bg-amber-500/15 border-amber-500/30 text-amber-300';
    label = `Expire dans ${jours}j`;
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${bg}`}>
      {label}
    </span>
  );
}

function ModalRenouvellement({ onClose, onSuccess, estEssai = false }) {
  const [periode, setPeriode] = useState('mensuel');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post('/mon-abonnement/demande-renouvellement', { periode, message });
      toast.success('Demande envoyée — l\'administrateur va te recontacter');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full p-5">
        <div className="flex items-center gap-3 mb-4">
          <Send size={18} className="text-yellow-400" />
          <h3 className="text-white font-semibold">{estEssai ? 'Passer en Premium' : 'Demande de renouvellement'}</h3>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {estEssai
            ? 'Choisis la formule Premium souhaitée. L\'administrateur recevra ta demande, activera ton Premium et te recontactera.'
            : 'Indique la durée souhaitée et un message si tu veux préciser un mode de paiement. L\'administrateur recevra ta demande et te recontactera.'}
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Période</label>
            <div className="flex gap-2">
              <button
                onClick={() => setPeriode('mensuel')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                  periode === 'mensuel'
                    ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-200'
                    : 'bg-gray-800 border-gray-700 text-gray-400'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setPeriode('annuel')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm border ${
                  periode === 'annuel'
                    ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-200'
                    : 'bg-gray-800 border-gray-700 text-gray-400'
                }`}
              >
                Annuel
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Message (facultatif)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex: je paie par Orange Money le 5 du mois"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-40
                       text-gray-900 text-sm font-medium rounded-lg flex items-center gap-2"
          >
            {loading ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MonAbonnement() {
  const { canEditParametres } = useAuth();
  const [info, setInfo] = useState(null);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRenew, setShowRenew] = useState(false);

  const charger = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/mon-abonnement');
      setInfo(data);
      if (canEditParametres && data.a_un_abonnement) {
        const pa = await api.get('/mon-abonnement/paiements');
        setPaiements(pa.data || []);
      }
    } catch (err) {
      toast.error('Échec du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);  // eslint-disable-line

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader size={24} className="animate-spin text-yellow-400" />
      </div>
    );
  }

  if (!info?.a_un_abonnement) {
    return (
      <div className="p-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex items-center gap-3">
          <AlertCircle className="text-amber-400" size={20} />
          <p className="text-gray-300 text-sm">
            Aucun abonnement actif n'est associé à ton commerce. Contacte l'administrateur.
          </p>
        </div>
      </div>
    );
  }

  const plan = info.plan || {};
  const devise = info.devise || 'GNF';
  const estEssai = (info.plan_code || plan.code) === 'essai';

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl mx-auto">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
            <CreditCard size={18} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Mon abonnement</h1>
            <p className="text-xs text-gray-500">
              Plan en cours, échéance, historique de paiements
            </p>
          </div>
        </div>
        {canEditParametres && (
          <button
            onClick={() => setShowRenew(true)}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600
                       text-gray-900 text-sm font-medium rounded-lg"
          >
            <Send size={14} /> {estEssai ? 'Passer en Premium' : 'Demander un renouvellement'}
          </button>
        )}
      </header>

      {/* Bandeau Essai */}
      {estEssai && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
          <Sparkles className="text-amber-300 mt-0.5 shrink-0" size={18} />
          <div className="flex-1">
            <p className="text-amber-200 text-sm font-medium">
              Période d'essai gratuite{(info.jours_restants !== null && info.jours_restants !== undefined && info.jours_restants >= 0)
                ? ` — ${info.jours_restants} jour${info.jours_restants > 1 ? 's' : ''} restant${info.jours_restants > 1 ? 's' : ''}`
                : info.statut === 'lecture_seule' ? ' — terminée' : ''}.
            </p>
            <p className="text-amber-200/80 text-xs mt-0.5">
              Tu profites de toutes les fonctionnalités. À la fin de l'essai, l'accès passe en lecture seule.
              Passe en Premium pour continuer sans interruption (bouton en haut à droite).
            </p>
          </div>
        </div>
      )}

      {/* Carte du plan */}
      <div className="bg-gradient-to-br from-yellow-500/10 to-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-yellow-400" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Plan en cours</span>
            </div>
            <h2 className="text-2xl font-bold text-white">{plan.nom || info.plan_nom}</h2>
            {plan.description && (
              <p className="text-sm text-gray-400 mt-1">{plan.description}</p>
            )}
          </div>
          <CouleurStatut statut={info.statut} jours={info.jours_restants} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat label="Prix mensuel" value={estEssai ? 'Gratuit' : formatMontant(plan.prix_mensuel, devise)} />
          <Stat label="Prix annuel"  value={estEssai ? '—' : formatMontant(plan.prix_annuel, devise)} />
          <Stat label="Boutiques max" value={plan.max_boutiques || '—'} icon={<Building2 size={12} />} />
          <Stat label="Utilisateurs max" value={plan.max_utilisateurs || '—'} icon={<Users size={12} />} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          <Stat label="Début" value={formatDate(info.date_debut)} icon={<Calendar size={12} />} />
          <Stat label="Échéance" value={formatDate(info.date_expiration)} icon={<Calendar size={12} />} />
          <Stat label="Jours restants"
                value={info.jours_restants !== null && info.jours_restants !== undefined
                  ? `${info.jours_restants} j${info.jours_grace_restants ? ` (+${info.jours_grace_restants}j grâce)` : ''}`
                  : '—'} />
        </div>
      </div>

      {/* Historique paiements */}
      {canEditParametres && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
            <History size={16} className="text-gray-400" />
            <h3 className="font-semibold text-white text-sm">Historique des paiements</h3>
            <span className="text-xs text-gray-500">({paiements.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-800/40">
                <tr className="text-left text-xs text-gray-400">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium text-right">Montant</th>
                  <th className="px-3 py-2 font-medium">Période</th>
                  <th className="px-3 py-2 font-medium">Mode</th>
                  <th className="px-3 py-2 font-medium">Référence</th>
                </tr>
              </thead>
              <tbody>
                {paiements.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-gray-500 py-6">
                    Aucun paiement enregistré
                  </td></tr>
                ) : (
                  paiements.map((p) => (
                    <tr key={p.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                      <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                        {formatDate(p.date_paiement)}
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-400 tabular-nums font-medium">
                        {formatMontant(p.montant, devise)}
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-xs whitespace-nowrap">
                        {p.periode_debut ? `${formatDate(p.periode_debut)} → ${formatDate(p.periode_fin)}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-xs capitalize">
                        {p.mode_paiement || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs font-mono">
                        {p.reference || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Conseil */}
      {info.statut !== 'actif' && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
          <CheckCircle2 size={16} className="text-amber-300 mt-0.5" />
          <p className="text-xs text-amber-200">
            Pour éviter le passage en lecture seule, anticipe ton renouvellement
            au moins quelques jours avant l'échéance.
          </p>
        </div>
      )}

      {showRenew && (
        <ModalRenouvellement
          estEssai={estEssai}
          onClose={() => setShowRenew(false)}
          onSuccess={() => { setShowRenew(false); charger(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded p-2">
      <p className="text-xs text-gray-500 flex items-center gap-1">
        {icon}{label}
      </p>
      <p className="text-sm text-white font-medium mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}
